<template>
  <div>
    <!-- Media -->
    <b-media class="mb-2 d-flex align-items-center">
      <template #aside>
        <b-avatar
          ref="previewEl"
          :src="userData.avatar"
          :text="avatarText(userData.fullName)"
          :variant="`light-${resolveUserRoleVariant(userData.role)}`"
          size="90px"
          rounded
        />
      </template>
      <h4 class="mb-1 capitalized">
        {{ userData.fullName }}
      </h4>
    </b-media>

    <!-- User Info: Input Fields -->
    <b-form>
      <b-row>
        <!-- Field: First Name -->
        <b-col cols="12" md="4">
          <b-form-group :label="$t('firstName')" label-for="first-name">
            <b-form-input id="first-name" v-model="userData.firstName" class="capitalized" />
          </b-form-group>
        </b-col>
        <!-- Field: Last Name -->
        <b-col cols="12" md="4">
          <b-form-group :label="$t('lastName')" label-for="last-name">
            <b-form-input id="last-name" v-model="userData.lastName" class="capitalized" />
          </b-form-group>
        </b-col>
        <!-- Field: Mobile -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('mobile')" label-for="mobile">
            <b-form-input id="mobile" v-model="userData.mobile" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <!-- Field: Email -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('email')" label-for="email">
            <b-form-input id="email" v-model="userData.email" type="email" />
          </b-form-group>
        </b-col>
        <!-- Field: LOVE -->
        <b-col v-if="appConfig.type === 'hw-admin'" cols="12" md="6" lg="4">
          <b-form-group label="Love" label-for="user-love">
            <v-select
              v-model="userData.love"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="loveOptions"
              :reduce="val => val.value"
              :clearable="false"
              input-id="user-love"
              class="capitalized"
            />
          </b-form-group>
        </b-col>

        <!-- Field: LOVE granted -->
        <b-col v-if="appConfig.type === 'hw-admin'" cols="12" md="6" lg="4">
          <b-form-group label="Love myönnetty">
            <flat-pickr
              v-model="loveGranted"
              class="form-control"
              :config="{ dateFormat: 'd.m.Y' }"
              placeholder="DD.MM.YYYY"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Role -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('profession')" label-for="user-role">
            <v-select
              v-model="userData.role"
              style="text-transform: capitalize"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="roleOptions"
              :clearable="false"
              input-id="user-role"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <!-- Field: Car Available -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('carAvailable')" label-for="car-available">
            <v-select
              v-model="userData.carAvailable"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="carAvailableOptions"
              :reduce="val => val.value"
              :clearable="false"
              input-id="car-available"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Driving License -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('drivingLicense')" label-for="driving-license">
            <v-select
              v-model="userData.drivingLicense"
              style="text-transform: capitalize;"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="drivingLicenseOptions"
              :reduce="val => val.value"
              :clearable="false"
              input-id="driving-license"
              label="label"
              :multiple="true"
              :placeholder="$t('select_driving_license')"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Block Button -->
        <b-col v-if="appConfig.type === 'hw-admin'" cols="12" md="6" lg="4" style="margin-top: 21px;">
          <b-button
            v-b-modal.block-partial-modal
            variant="danger"
            :disabled="userData.status === 'blocked' || blockLoading"
          >
            <b-spinner v-if="blockLoading" small />
            {{ $t('personel.btn.block') }}
          </b-button>
          <b-button
            v-if="
              userData.status === 'blocked' || (userData.blockedFrom && userData.blockedFrom.length)
            "
            v-b-modal.unblock-partial-modal
            :disabled="unblockLoading"
            variant="outline-danger"
            class="ml-1"
          >
            <b-spinner v-if="unblockLoading" small />
            {{ $t('personel.btn.unblock') }}
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <!-- Field: Profession -->
        <b-col cols="12" md="6" lg="4">
          <b-form-group :label="$t('personel.labels.degree')" label-for="user-profession">
            <v-select
              v-model="userData.profession"
              style="text-transform: capitalize"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="professionOptions"
              :clearable="false"
              input-id="user-profession"
            />
          </b-form-group>
        </b-col>

        <!-- Field: studies.studyType -->
        <b-col v-if="userData.profession === 'opiskelija'" cols="12" md="6" lg="4">
          <b-form-group :label="$t('personel.labels.typeOfStudy')" label-for="study-type">
            <v-select
              v-model="userData.studies.studyType"
              :options="studyTypeOptions"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :reduce="val => val.value"
              :clearable="false"
              input-id="user-study-type"
            />
          </b-form-group>
        </b-col>

        <!-- Field: studies.credits -->
        <b-col v-if="userData.profession === 'opiskelija'" cols="12" md="6" lg="4">
          <b-form-group :label="$t('personel.labels.credits')" label-for="study-credits">
            <b-form-input
              id="study-credits"
              v-model.number="userData.studies.credits"
              class="capitalized"
              type="number"
            />
          </b-form-group>
          <div v-if="canWorkAs">
            <p :style="'color:' + (canWorkAs.value ? 'green' : 'red')">
              {{ canWorkAs.text }}
            </p>
          </div>
        </b-col>

        <!-- Field: distraint -->
        <b-col cols="12" md="6" lg="4">
          <b-form-checkbox v-model="distraint" style="margin-top: 30px">
            {{ $t('distraint') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-form>

    <!-- Action Buttons -->
    <div class="mt-2">
      <b-button
        variant="primary"
        class="mb-1 mb-sm-0 mr-0 mr-sm-1"
        :block="$store.getters['app/currentBreakPoint'] === 'xs'"
        @click="saveChanges"
      >
        {{ $t('personel.btn.save_changes') }}
      </b-button>
      <b-button
        variant="outline-secondary"
        type="reset"
        :block="$store.getters['app/currentBreakPoint'] === 'xs'"
        :to="{ name: 'apps-personels-list' }"
      >
        {{ $t('personel.btn.close') }}
      </b-button>
    </div>

    <b-modal
      id="block-partial-modal"
      centered
      :title="$t('block_employee')"
      :ok-title="$t('block')"
      :cancel-title="$t('cancel')"
      :ok-variant="userData.status === 'accepted' ? 'danger' : 'outline-danger'"
      modal-class="modal-danger"
      no-close-on-backdrop
      :ok-disabled="
        userData.status === 'accepted' && (!blockReason || (blockType === 'area' && !blockId))
      "
      @ok="handlePartialBlock('block')"
      @hide="resetPartialBlockForm"
    >
      <b-form-group :label="$t('blockFrom')" label-for="block-type">
        <v-select
          v-model="blockType"
          :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
          :options="blockTypeOptions"
          :reduce="val => val.value"
          :clearable="false"
          class="capitalized"
          :disabled="userData.status !== 'accepted'"
        />
      </b-form-group>
      <b-form-group v-if="blockType !== 'all'">
        <b-form-input
          v-if="blockType === 'account'"
          id="full-name"
          :value="selectedAccount.value.name"
          class="capitalized"
          disabled
        />
        <v-select
          v-if="blockType === 'area'"
          v-model="blockId"
          :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
          :options="areaOptions"
          :reduce="val => val.value"
          :clearable="false"
          multiple
          class="capitalized"
          @option:deselected="blockId = null"
        />
        <small v-if="blockType === 'area' && !blockId" class="text-danger">
          {{ $t('personel.block_modal.block_area_warning') }}
        </small>
      </b-form-group>
      <b-form-group
        v-if="userData.status === 'accepted'"
        :label="$t('block_reason')"
        label-for="block-reason"
      >
        <b-form-textarea
          id="block-reason"
          v-model="blockReason"
          rows="4"
          max-rows="4"
          :placeholder="$t('personel.block_modal.block_reason_placeholder')"
        />
        <small v-if="!blockReason" class="text-danger">{{
          $t('personel.block_modal.block_reason_warning')
        }}</small>
      </b-form-group>
      <p v-else>
        {{ $t('personel.block_modal.block_warning') }}
      </p>
    </b-modal>

    <b-modal
      id="unblock-partial-modal"
      centered
      :title="$t('remove_block')"
      :ok-title="$t('ok')"
      :cancel-title="$t('cancel')"
      ok-variant="outline-danger"
      modal-class="modal-danger"
      no-close-on-backdrop
      :ok-disabled="disabledUnblock"
      @shown="getDataUnblock"
      @ok="handlePartialBlock('unblock')"
      @hide="resetPartialUnlockForm"
    >
      <b-form-group v-if="userData.status === 'blocked'">
        <b-form-checkbox v-model="unblockApplication" :value="true" :unchecked-value="false">
          {{ $t('personel.unblock_modal.labels.unblockAccount') }}
        </b-form-checkbox>
      </b-form-group>
      <template v-if="userData.blockedFrom && userData.blockedFrom.length > 0">
        <b-form-group
          v-if="userData.blockedFrom.some(item => item.type === 'area')"
          :label="$t('personel.unblock_modal.labels.select_areas')"
        >
          <v-select
            v-model="unblockAreas"
            :dir="$store.state.hwUiLayout.isRTL ? 'lrt' : 'ltr'"
            :options="areaUnblockOptions"
            :reduce="val => val.value"
            :clearable="false"
            multiple
            class="capitalized"
          />
        </b-form-group>
        <b-form-group
          v-if="userData.blockedFrom.some(item => item.type === 'account')"
          :label="$t('personel.unblock_modal.labels.selectAccounts')"
        >
          <v-select
            v-model="unblockAccounts"
            :dir="$store.state.hwUiLayout.isRTL ? 'lrt' : 'ltr'"
            :options="accountUnblockOptions"
            :reduce="val => val.value"
            :clearable="false"
            multiple
            class="capitalized"
          />
        </b-form-group>
      </template>
      <p>
        {{ $t('personel.block_modal.block_warning') }}
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BMedia,
  BAvatar,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BForm,
  BSpinner,
  BModal,
  BFormCheckbox,
} from 'bootstrap-vue'
import { avatarText } from '@hw-ui/utils/filter'
import i18n from '@hw-core/libs/i18n'
import vSelect from 'vue-select'
import { useInputImageRenderer } from '@hw-ui/comp-functions/forms/form-utils'
import flatPickr from 'vue-flatpickr-component'
import { ref, computed, watch } from '@vue/composition-api'
import store from '@/shared/store'
import { db } from '@/shared/services/firebase'
// eslint-disable-next-line no-unused-vars
import usePersonelList from '../personel-list/usePersonelList'

export default {
  components: {
    BButton,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BForm,
    BModal,
    BFormCheckbox,
    BSpinner,
    vSelect,
    flatPickr,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      distraint: this.userData.specials
        ? this.userData.specials.distraint
        : false,
    }
  },
  computed: {
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
    roleOptions() {
      return this.$store.state.app.appConfig.type === 'hw-admin'
        ? store.state.appAccount.roles.map(role => role.label)
        : store.state.appAccount.selectedAccount.value.professions
    },
    professionOptions() {
      return store.state.appAccount.roles.map(role => role.label).concat(['opiskelija'])
    }
  },
  setup(props, { emit }) {
    const { resolveUserRoleVariant } = usePersonelList()
    const blockLoading = ref(false)
    const unblockLoading = ref(false)
    const unblockApplication = ref(false)
    const unblockAreas = ref([])
    const unblockAccounts = ref([])
    const blockReason = ref('')
    const accountUnblockOptions = ref([])
    const areaUnblockOptions = ref([])
    const loveGranted = ref(props.userData.loveGranted ? `${new Date(props.userData.loveGranted.toDate()).getDate()}.${new Date(props.userData.loveGranted.toDate()).getMonth() + 1}.${new Date(props.userData.loveGranted.toDate()).getFullYear()}` : '')

    const canWorkAs = computed(() => {
      if (props.userData.profession !== 'opiskelija') {
        return ''
      }
      if (props.userData.studies.studyType === 'lähihoitajaopiskelija') {
        if (props.userData.studies.credits >= 30) {
          return { text: i18n.t('work_as_types.practice_nurse'), value: 'lähihoitaja' }
        }
        return { text: i18n.t('work_as_types.not_enough_study_credit'), value: '' }
      }
      if (props.userData.studies.studyType === 'sairaanhoitajaopiskelija') {
        if (props.userData.studies.credits >= 60 && props.userData.studies.credits < 140) {
          return { text: i18n.t('work_as_types.practice_nurse'), value: 'lähihoitaja' }
        }
        if (props.userData.studies.credits >= 140) {
          return { text: i18n.t('work_as_types.normal_nurse'), value: 'sairaanhoitaja' }
        }
        return { text: i18n.t('work_as_types.not_enough_study_credit'), value: '' }
      }
      return null
    })

    const loveOptions = [
      { label: i18n.t('completed'), value: true },
      { label: i18n.t('not_completed'), value: false },
    ]

    const carAvailableOptions = [
      { label: 'Kyllä', value: true },
      { label: i18n.t('no'), value: false },
    ]

    const drivingLicenseOptions = [
      { label: 'B', value: 'b' },
      { label: 'C', value: 'c' },
      { label: 'A', value: 'a' },
    ]

    const permissionsData = [
      {
        module: 'Admin',
        read: true,
        write: false,
        create: false,
        delete: false,
      },
      {
        module: 'Staff',
        read: false,
        write: true,
        create: false,
        delete: false,
      },
      {
        module: 'Author',
        read: true,
        write: false,
        create: true,
        delete: false,
      },
      {
        module: 'Contributor',
        read: false,
        write: false,
        create: false,
        delete: false,
      },
      {
        module: 'User',
        read: false,
        write: false,
        create: false,
        delete: true,
      },
    ]

    const studyTypeOptions = ref([
      { label: i18n.t('students.practice_nurse'), value: 'lähihoitajaopiskelija' },
      { label: i18n.t('students.normal_nurse'), value: 'sairaanhoitajaopiskelija' },
    ])

    // ? Demo Purpose => Update image on click of update
    const refInputEl = ref(null)
    const previewEl = ref(null)

    const { inputImageRenderer } = useInputImageRenderer(refInputEl, base64 => {
      // eslint-disable-next-line no-param-reassign
      props.userData.avatar = base64
    })

    const blockType = ref(props.userData.status === 'accepted' ? 'account' : 'all')
    const blockTypeOptions = [
      { label: i18n.t('block_types.from_all_area'), value: 'account' },
      { label: i18n.t('block_types.select_area'), value: 'area' },
      { label: i18n.t('block_types.all'), value: 'all' },
    ]

    const areaOptions = ref([])
    const blockId = ref(null)

    const selectedAccount = computed(() => store.state.appAccount.selectedAccount)

    let processer = {}

    store.dispatch('appAuth/getCurrentUser').then(res => {
      const user = JSON.parse(JSON.stringify(res))
      processer = {
        firstName: user.firstName,
        lastName: user.lastName,
      }
    })

    const saveChanges = () => {
      // Handle here student conversion
      if (
        props.userData.profession === 'opiskelija' &&
        props.userData.studies.studyType &&
        props.userData.studies.credits
      ) {
        const role = canWorkAs.value.value
        // eslint-disable-next-line no-param-reassign
        props.userData.role = role
        // eslint-disable-next-line no-param-reassign
        props.userData.studies = {
          credits: parseInt(props.userData.studies.credits, 10),
          studyType: props.userData.studies.studyType,
        }
      }
      // eslint-disable-next-line no-param-reassign
      if (loveGranted.value !== '') {
        // eslint-disable-next-line no-param-reassign
        props.userData.loveGranted = new Date(`${loveGranted.value.slice(3, 5)}-${loveGranted.value.slice(0, 2)}-${loveGranted.value.slice(7)} 00:00:00`)
      }
      emit('onSaveChanges')
    }

    const toggleBlock = (blockedFrom, blockStatus) => {
      let params = {
        blockStatus,
        blockedFrom,
        blockReason: blockReason.value,
        userId: props.userData.uid,
        callback: () => {
          if (blockStatus === 'block') {
            // eslint-disable-next-line no-param-reassign
            props.userData.status = 'blocked'
            // eslint-disable-next-line no-param-reassign
            props.userData.confirmed = false
          } else {
            // eslint-disable-next-line no-param-reassign
            props.userData.status = 'accepted'
            // eslint-disable-next-line no-param-reassign
            props.userData.confirmed = true
            // eslint-disable-next-line no-param-reassign
            props.userData.blockedFrom = blockedFrom
            unblockAreas.value = blockedFrom.filter(i => i.type === 'area').map(i => i.blockId)
          }
          blockLoading.value = false
          unblockLoading.value = false
        },
      }

      // For unblocking user
      if (blockStatus === 'block') {
        params = {
          ...params,
          blockReason: blockReason.value,
        }
      }

      emit('blockUser', params)
    }

    watch(
      () => props.userData.status,
      newVal => {
        if (newVal !== 'accepted') {
          blockType.value = 'all'
        }
      }
    )

    watch(
      () => [props.userData.firstName, props.userData.lastName],
      () => {
        // firstname and lastname and fullname as lower case
        // eslint-disable-next-line no-param-reassign
        props.userData.firstName = props.userData.firstName.toLowerCase()
        // eslint-disable-next-line no-param-reassign
        props.userData.lastName = props.userData.lastName.toLowerCase()
        // eslint-disable-next-line no-param-reassign
        props.userData.fullName = `${props.userData.firstName} ${props.userData.lastName}`
      }
    )

    const handlePartialBlock = (type = 'block') => {
      let blockStatus = props.userData.status === 'accepted' ? 'unblock' : 'block'
      if (type === 'block' && blockType.value === 'all') {
        blockStatus = 'block'
      }

      const user = props.userData
      let blockedFrom = []
      const oldBlocks = user.blockedFrom ? user.blockedFrom : []

      if (type === 'block') {
        blockLoading.value = true
        if (blockType.value === 'account') {
          blockedFrom = oldBlocks.concat([
            {
              type: 'account',
              blockId: selectedAccount.value.value.id,
              blockReason: blockReason.value,
              processer: `${processer.firstName} ${processer.lastName}`,
              date: new Date(),
            },
          ])
        }

        if (blockType.value === 'area') {
          const newAreaState = blockId.value.map(id => ({
            type: 'area',
            blockId: id,
            blockReason: blockReason.value,
            processer: `${processer.firstName} ${processer.lastName}`,
            date: new Date(),
          }))
          blockedFrom = oldBlocks.concat(newAreaState)
        }
      } else {
        unblockLoading.value = true

        // Unblock accounts
        // Remove account in blockFrom
        if (unblockAccounts.value.length > 0) {
          oldBlocks.forEach(item => {
            if (item.type === 'account' && !unblockAccounts.value.includes(item.blockId)) {
              blockedFrom.push(item)
            }
          })
        } else {
          blockedFrom = blockedFrom.concat(oldBlocks.filter(i => i.type === 'account'))
        }

        // Unblock areas
        // Remove area in blockFrom
        if (unblockAreas.value.length > 0) {
          oldBlocks.forEach(item => {
            if (item.type === 'area' && !unblockAreas.value.includes(item.blockId)) {
              blockedFrom.push(item)
            }
          })
        } else {
          blockedFrom = blockedFrom.concat(oldBlocks.filter(i => i.type === 'area'))
        }
        if (unblockApplication.value) {
          blockStatus = 'unblock'
        }
      }
      blockedFrom = blockedFrom.filter(
        (v, i, a) => a.findIndex(v2 => ['type', 'blockId'].every(k => v2[k] === v[k])) === i
      )
      toggleBlock(blockedFrom, blockStatus)
    }

    const resetPartialBlockForm = () => {
      blockType.value = 'account'
      blockId.value = null
      blockReason.value = ''
    }

    const resetPartialUnlockForm = () => {
      unblockAreas.value = []
      unblockAccounts.value = []
    }

    const getAreaListWithUserSelected = () => {
      db.collection('areas')
        .where('accountId', '==', selectedAccount.value.value.id)
        .get()
        .then(snapshot => {
          areaOptions.value = snapshot.docs.map(doc => {
            const item = doc.data()
            item.id = doc.id
            return {
              value: doc.id,
              label: item.name,
            }
          })
        })
    }

    const getAreaById = id =>
      db
        .collection('areas')
        .doc(id)
        .get()
        .then(doc => ({ id: doc.id, ...doc.data() }))

    const getAccountById = id =>
      db
        .collection('accounts')
        .doc(id)
        .get()
        .then(doc => ({ id: doc.id, ...doc.data() }))

    const getDataUnblock = async () => {
      const accountIds = props.userData.blockedFrom.reduce(
        (prevState, item) => (item.type === 'account' ? [...prevState, item.blockId] : prevState),
        []
      )
      const areaIds = props.userData.blockedFrom.reduce(
        (prevState, item) => (item.type === 'area' ? [...prevState, item.blockId] : prevState),
        []
      )

      accountUnblockOptions.value = await Promise.all(
        accountIds.map(accountId => getAccountById(accountId))
      ).then(docs => docs.map(i => ({ label: i.name, value: i.id })))

      areaUnblockOptions.value = await Promise.all(
        areaIds.map(areaId => getAreaById(areaId))
      ).then(docs => docs.map(i => ({ label: i.name, value: i.id })))
      unblockApplication.value = false
    }

    watch(blockType, newVal => {
      if (newVal === 'area') {
        getAreaListWithUserSelected()
      }
    })

    const disabledUnblock = computed(() => {
      let isDisabled = true

      if (props.userData.status === 'blocked' && unblockApplication.value) {
        isDisabled = false
      } else if (
        props.userData.blockedFrom &&
        props.userData.blockedFrom.some(item => item.type === 'area') &&
        unblockAreas.value.length > 0
      ) {
        isDisabled = false
      } else if (
        props.userData.blockedFrom &&
        props.userData.blockedFrom.some(item => item.type === 'account') &&
        unblockAccounts.value.length > 0
      ) {
        isDisabled = false
      }
      return isDisabled
    })

    return {
      blockLoading,
      blockReason,
      loveGranted,
      resolveUserRoleVariant,
      avatarText,
      loveOptions,
      carAvailableOptions,
      drivingLicenseOptions,
      permissionsData,

      getAreaListWithUserSelected,
      unblockLoading,
      unblockApplication,
      unblockAreas,
      unblockAccounts,
      accountUnblockOptions,
      areaUnblockOptions,
      getDataUnblock,
      disabledUnblock,
      resetPartialUnlockForm,

      //  ? Demo - Update Image on click of update button
      refInputEl,
      previewEl,
      inputImageRenderer,

      selectedAccount,
      blockType,
      blockTypeOptions,
      areaOptions,
      blockId,
      handlePartialBlock,
      resetPartialBlockForm,
      saveChanges,
      studyTypeOptions,
      canWorkAs,
    }
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';
.capitalized {
  text-transform: capitalize;
}
</style>
